import { forwardRef, type JSX, useCallback, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react'

import { useGSAP } from '@/composables/useGSAP'

import UIIcon from '../UI/UIIcon'

export type ButtonVariant = 'default' | 'secondary' | 'tertiary' | 'back' | 'question' | 'round' | 'link'

export type ButtonProps = {
  className?: string
  children?: string | JSX.Element | JSX.Element[]

  onClick?: () => void
  onAppeared?: (element: HTMLElement) => void
  type?: 'button' | 'submit' | 'reset'
  is?: 'button' | 'label' | 'a'

  rejected?: boolean
  disabled?: boolean
  active?: boolean
  size?: 'small' | 'default'

  variant?: ButtonVariant
  hasParticles?: boolean
  appear?: boolean
  appearDelay?: number
  from?: 'top' | 'bottom'
  questionState?: 'default' | 'correct' | 'incorrect'

  ariaCurrent?: boolean
  ariaLabel?: string

  target?: string
  href?: string
  rel?: string
  animate?: boolean
}

export type ButtonRef = {
  exit: () => void
}

const Button = forwardRef<ButtonRef, ButtonProps>(
  (
    {
      className,
      children,

      onClick,
      onAppeared,

      rejected,
      disabled,

      type = undefined,
      is = 'button',
      active,
      variant = 'default',
      questionState = 'default',
      size = 'default',

      hasParticles = false,
      appear = false,
      appearDelay = 0,
      ariaCurrent = undefined,
      ariaLabel = undefined,
      from = 'bottom',

      target = null,
      href = null,
      rel = null,
      animate = true,
    },
    ref
  ) => {
    const Component = is
    const buttonRef = useRef(null)

    const { gsap } = useGSAP()

    const props =
      is === 'a' ? { href, target, rel: rel ?? (target === '_blank' ? 'noopener noreferrer' : undefined) } : {}

    const _onClick = useCallback(() => {
      onClick?.()
    }, [onClick])

    const variantClassName = (() => {
      switch (variant) {
        case 'back':
          return 'text-cta-s'
        default:
          return 'text-cta'
      }
    })()
    const variantBackgroundClassName = (() => {
      switch (variant) {
        case 'back':
          return 'border-2 border-black-blue'
        case 'secondary':
        case 'round':
          return 'border-2 border-coral'
        case 'tertiary':
          return 'bg-peach'
        case 'link':
          return ''
        default:
          return 'bg-coral'
      }
    })()
    const variantBackgroundHoverClassName = (() => {
      switch (variant) {
        case 'back':
          return 'bg-black-blue'
        case 'link':
          return ''
        default:
          return 'border-4 border-black-blue'
      }
    })()
    const variantBackgroundActiveClassName = (() => {
      switch (variant) {
        default:
          return 'bg-peach'
      }
    })()
    const variantInnerClassName = (() => {
      switch (variant) {
        case 'back':
          return 'px-16 py-0 min-h-36'
        case 'question':
          return `px-40 md:px-48 py-0 min-h-42 md:min-h-52 ${questionState === 'correct' && 'text-cream'}`
        case 'round':
          return 'min-w-52 min-h-52'
        default:
          return size === 'small' ? 'px-20 py-8 min-h-36' : 'px-32 py-0 min-h-52'
      }
    })()
    const variantInnerHoverClassName = (() => {
      switch (variant) {
        case 'back':
          return 'text-cream group-active:text-black-blue group-[.active]:text-black-blue'
        // case 'question':
        //   return 'text-cream';
        default:
          return ''
      }
    })()

    const background = useRef<HTMLElement>(null)
    const inner = useRef<HTMLElement>(null)

    useLayoutEffect(() => {
      const isRound = variant === 'round'
      if (!animate) return
      const ctx = gsap.context(() => {
        if (appear) {
          const tl = gsap.timeline({
            delay: appearDelay,
            onComplete: () => {
              onAppeared?.(buttonRef.current)
              // buttonRef.current.blur();
            },
          })
          if (background.current) {
            tl.fromTo(
              background.current,
              {
                clipPath: 'circle(0% at 50% 110%)',
              },
              {
                clipPath: !isRound ? 'circle(80% at 50% 100%)' : 'circle(100% at 50% 100%)',
                duration: 0.7,
                ease: 'power4.inOut',
              },
              0
            )
          }
          if (inner.current) {
            tl.fromTo(
              inner.current,
              {
                yPercent: 100,
              },
              {
                yPercent: 0,
                duration: 0.7,
                ease: 'power4.inOut',
              },
              0
            )
          }
        }
      })

      return () => ctx.kill()
    }, [appear, appearDelay, onAppeared, variant, buttonRef, gsap, background, inner, animate])

    const [isExited, setIsExited] = useState(false)
    const exit = useCallback(() => {
      if (!animate) return
      gsap
        .timeline({
          onComplete: () => {
            setIsExited(true)
          },
        })
        .fromTo(
          background.current,
          {
            clipPath: 'circle(80% at 50% 100%)',
          },
          {
            clipPath: 'circle(0% at 50% 110%)',
            duration: 0.5,
            ease: 'power4.inOut',
          },
          0
        )
        .fromTo(
          inner.current,
          {
            yPercent: 0,
          },
          {
            yPercent: 100,
            duration: 0.5,
            ease: 'power4.inOut',
          },
          0
        )
    }, [gsap])

    useImperativeHandle(
      ref,
      () => ({
        exit,
      }),
      [exit]
    )

    return (
      !isExited && (
        <Component
          ref={buttonRef}
          className={`Button relative inline-block select-none ${variantClassName} ${active ? 'active' : ''} ${variant === 'question' && questionState !== 'default' ? 'answered' : ''} ${variant === 'question' && questionState === 'correct' ? 'correct' : ''} ${variant === 'question' && questionState === 'incorrect' ? 'incorrect' : ''} focus-visible:outline rounded-64 focus-visible:outline-2 outline-scarlet outline-offset-2 group ${className ? className : ''} ${disabled && 'pointer-events-none text-slate/50 '}`}
          onClick={_onClick}
          disabled={disabled}
          type={type ? type : is === 'button' ? 'button' : undefined}
          aria-current={ariaCurrent}
          aria-label={ariaLabel}
          {...props}
        >
          <span ref={background} className='Button__Background absolute top-0 left-0 w-full h-full rounded-inherit'>
            <span
              aria-hidden={true}
              className={`Button__Background__Inner absolute top-0 left-0 w-full h-full rounded-inherit ${variantBackgroundClassName} ${disabled && 'bg-slate/20'} group-active:opacity-0 group-[.active]:opacity-0 transition-opacity duration-100 ease-out-quad`}
            />
            <span
              aria-hidden={true}
              className={`Button__Background__Hover absolute top-0 left-0 w-full h-full rounded-inherit ${variantBackgroundHoverClassName} opacity-0 group-hover:opacity-100 transition-opacity duration-300 ease-out-quad group-active:opacity-0 group-[.active]:opacity-0`}
            />
            <span
              aria-hidden={true}
              className={`Button__Background__Active absolute top-0 left-0 w-full h-full rounded-inherit ${variantBackgroundActiveClassName} opacity-0 group-active:opacity-100 group-[.active]:opacity-100 transition-opacity duration-100 ease-out-quad`}
            />
            {variant === 'question' && (
              <span
                aria-hidden={true}
                className={`Button__Background__Correct flex items-center justify-start absolute top-0 left-0 w-full h-full rounded-inherit bg-mid-blue opacity-0 group-[.correct]:opacity-100 transition-opacity duration-100 ease-out-quad`}
              >
                <span className='relative ml-10'>
                  <UIIcon icon='background1' className='text-24 md:text-32 text-light-blue' />
                  <UIIcon
                    icon='checked'
                    className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-8 text-cream'
                  />
                </span>
              </span>
            )}
            {variant === 'question' && (
              <span
                aria-hidden={true}
                className={`Button__Background__Incorrect flex items-center justify-start absolute top-0 left-0 w-full h-full rounded-inherit bg-peach opacity-0 group-[.incorrect]:opacity-100 transition-opacity duration-100 ease-out-quad`}
              >
                <span className='relative ml-10'>
                  <UIIcon icon='background2' className='text-24 md:text-32 text-coral' />
                  <UIIcon icon='close' className='absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-8' />
                </span>
              </span>
            )}
          </span>

          <span
            className={`Button__Inner flex justify-center items-center relative top-0 left-0 w-full h-full ${variantInnerClassName}`}
          >
            <span className='relative block overflow-hidden'>
              <span ref={inner} className='relative block overflow-hidden'>
                {children}
              </span>
            </span>
          </span>
        </Component>
      )
    )
  }
)

export default Button
